import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, getDocs, doc, getDoc, setDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { db } from './firebase';
import * as XLSX from 'xlsx';
import './HOInvoiceExport.css';

// ─────────────────────────────────────────────────────────────────────────────
// HOInvoiceExport
//
// PURPOSE:
//   Generates a QuickBooks-ready CSV containing all monthly invoices:
//     1. Royalty invoices (from monthEndSubmissions)
//     2. T-Shirt invoices (from tshirtOrders for the month)
//     3. Courier invoices (manually entered from BobGo)
//
//   Plus a Payment Breakdown XLSX for Helena to match incoming payments.
//
// SAFETY:
//   - Skips franchisees flagged isTest === true
//   - Skips franchisees without quickbooksCustomerName (shown in warning panel)
//   - Uses sequential invoice numbers stored in settings/invoicing
//   - User must tick to include each franchisee per tab
//   - Confirms before generating
//
// VAT MATH:
//   - BABS stores INC-VAT amounts (matches what franchisees see on SOD)
//   - QB CSV uses EX-VAT amounts (matches how Linda enters invoices manually)
//   - So: exVatAmount = incVatAmount / (1 + vatRate/100)
//   - For Michelle (vatRate=0): no conversion needed
// ─────────────────────────────────────────────────────────────────────────────

// QB Product/Service names - must match EXACTLY what's in QuickBooks
const QB_ITEMS = {
  royalty:        'SA Royalties',
  kidsTshirt:     '2026 Kids Tshirt',
  adultTshirtSL:  '2026 Adult T-shirt S-L',
  adultTshirtXL:  '2026 Adult XL-XXXL',
  stickers:       '2026 Stickers',
  courier:        'Courier cost',
};

// QB Product/Service prices (INC VAT - matches QB product setup)
const QB_PRICES_INC = {
  kidsTshirt:    120,
  adultTshirtSL: 120,
  adultTshirtXL: 130,
  stickers:      70,
};

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// ─── Helpers ────────────────────────────────────────────────────────────────
const roundMoney = (n) => Math.round((Number(n) || 0) * 100) / 100;

const vatDivisor = (vatRate) => 1 + ((vatRate || 0) / 100);

const formatDDMMYYYY = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  const day   = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${d.getFullYear()}`;
};

const csvEscape = (val) => {
  if (val === null || val === undefined) return '';
  const s = String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
};

const downloadFile = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const formatR = (n) => 'R ' + (Number(n) || 0).toFixed(2);

// Friendly "time ago" formatter for the draft save indicator
const timeAgo = (date) => {
  if (!date) return '';
  const seconds = Math.floor((new Date() - date) / 1000);
  if (seconds < 5)    return 'just now';
  if (seconds < 60)   return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return date.toLocaleDateString();
};

// Small auto-save indicator shown next to the Back button
function DraftStatusBadge({ status, savedAt }) {
  // Force a re-render every 30s so "Xs ago" stays accurate
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    if (status !== 'saved' || !savedAt) return;
    const t = setInterval(() => forceUpdate(v => v + 1), 30000);
    return () => clearInterval(t);
  }, [status, savedAt]);

  if (status === 'idle' && !savedAt) return null;
  if (status === 'saving')           return <span className="draft-badge saving">💾 Saving draft...</span>;
  if (status === 'error')            return <span className="draft-badge error">⚠️ Draft not saved</span>;
  if (status === 'saved' && savedAt) return <span className="draft-badge saved">💾 Draft saved {timeAgo(savedAt)}</span>;
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
function HOInvoiceExport({ onClose }) {
  const today = new Date();

  // Default to current month (user can change)
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth());
  const [selectedYear,  setSelectedYear]  = useState(today.getFullYear());

  // Data state
  const [loading, setLoading]             = useState(true);
  const [generating, setGenerating]       = useState(false);
  const [franchisees, setFranchisees]     = useState([]);     // array
  const [submissions, setSubmissions]     = useState({});     // by franchiseeId
  const [tshirtOrdersByF, setTshirtOrdersByF] = useState({}); // by franchiseeId -> array

  // Invoice settings (from settings/invoicing doc)
  const [invoicePrefix, setInvoicePrefix]         = useState('2023_');
  const [lastInvoiceNumber, setLastInvoiceNumber] = useState(4699);
  const [invoiceDate, setInvoiceDate]                 = useState(today.toISOString().slice(0, 10));
  const [courierInvoiceDate, setCourierInvoiceDate]   = useState(today.toISOString().slice(0, 10));
  const [termsDays, setTermsDays]                 = useState(30);

  // Per-row user state
  // royaltyState: { [franchiseeId]: { include: bool, amountOverride: string|null } }
  // tshirtState:  { [franchiseeId]: { include: bool } }
  // courierState: { [franchiseeId]: { include: bool, amountIncVat: string } }
  // tshirtQty:    { [franchiseeId]: { kids, adultSL, adultXL, stickers } } - editable quantities
  const [royaltyState, setRoyaltyState] = useState({});
  const [tshirtState,  setTshirtState]  = useState({});
  const [courierState, setCourierState] = useState({});
  const [tshirtQty,    setTshirtQty]    = useState({});

  // Active tab
  const [activeTab, setActiveTab] = useState('royalty');

  // ── Draft auto-save state ──────────────────────────────────────────────────
  // 'idle' | 'saving' | 'saved' | 'error'
  const [draftStatus, setDraftStatus]       = useState('idle');
  const [draftSavedAt, setDraftSavedAt]     = useState(null);  // Date object of last save

  const currentYear = today.getFullYear();
  const yearOptions = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];

  // ─── Load data on mount ────────────────────────────────────────────────────
  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (franchisees.length > 0) {
      loadMonthData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear, franchisees]);

  // ─── Auto-save draft (debounced) ────────────────────────────────────────────
  // Watches all draft-worthy state. 1.5s after the last change, persists to Firestore.
  // If state is empty (e.g. just navigated to a new month with no work), deletes any
  // stale draft. Cleared after a successful Generate.
  useEffect(() => {
    if (loading)                  return;
    if (franchisees.length === 0) return;

    // Capture current month/year so when the timer fires, we save to the right key
    // (the user might've changed month before the timer fires)
    const captureYear  = selectedYear;
    const captureMonth = selectedMonth;
    const captureState = {
      royaltyState, tshirtState, tshirtQty, courierState,
      invoiceDate, courierInvoiceDate, termsDays,
    };

    const hasContent =
      Object.keys(royaltyState).length > 0 ||
      Object.keys(tshirtState).length  > 0 ||
      Object.keys(tshirtQty).length    > 0 ||
      Object.keys(courierState).length > 0;

    const timer = setTimeout(() => {
      if (hasContent) {
        saveDraft(captureYear, captureMonth, captureState);
      } else {
        // No content to save — delete any stale draft for cleanliness
        deleteDraft(captureYear, captureMonth);
      }
    }, 1500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [royaltyState, tshirtState, tshirtQty, courierState, invoiceDate, courierInvoiceDate, termsDays, loading]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load franchisees (REQUIRED)
      const fSnap = await getDocs(collection(db, 'Franchisees'));
      const fList = fSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      fList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setFranchisees(fList);

      // Load invoicing settings (OPTIONAL - falls back to defaults if rules don't allow access)
      try {
        const settingsRef  = doc(db, 'settings', 'invoicing');
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists()) {
          const s = settingsSnap.data();
          if (s.invoicePrefix)     setInvoicePrefix(s.invoicePrefix);
          if (typeof s.lastInvoiceNumber === 'number') setLastInvoiceNumber(s.lastInvoiceNumber);
        }
      } catch (settingsErr) {
        console.warn('Could not load invoicing settings (using defaults). Add Firestore rules for /settings/ to persist invoice counter between sessions.', settingsErr.message);
      }
    } catch (err) {
      console.error('Initial load error:', err);
      alert('Could not load franchisees: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ─── Draft auto-save helpers ───────────────────────────────────────────────
  // Stores per-month "work in progress" in Firestore so the user doesn't lose
  // typed courier amounts, manual T-shirt qty, etc., if they refresh or come
  // back later. One draft per month/year combination.
  const draftDocId = (year, month) => `invoiceDraft_${year}_${month}`;

  const saveDraft = async (yearArg, monthArg, payload) => {
    const year  = yearArg  ?? selectedYear;
    const month = monthArg ?? selectedMonth;
    setDraftStatus('saving');
    try {
      await setDoc(doc(db, 'settings', draftDocId(year, month)), {
        ...payload,
        savedAt: Timestamp.now(),
      });
      setDraftStatus('saved');
      setDraftSavedAt(new Date());
    } catch (err) {
      console.warn('Draft save failed (rules may not allow /settings/ writes):', err.message);
      setDraftStatus('error');
    }
  };

  const deleteDraft = async (yearArg, monthArg) => {
    const year  = yearArg  ?? selectedYear;
    const month = monthArg ?? selectedMonth;
    try {
      await deleteDoc(doc(db, 'settings', draftDocId(year, month)));
      setDraftStatus('idle');
      setDraftSavedAt(null);
    } catch (err) {
      // Silently ignore - draft might not exist or rules may not allow
    }
  };

  const loadMonthData = async () => {
    setLoading(true);
    try {
      // ── Submissions for the selected month/year ──
      const subQ = query(
        collection(db, 'monthEndSubmissions'),
        where('month', '==', selectedMonth),
        where('year',  '==', selectedYear)
      );
      const subSnap = await getDocs(subQ);
      const subByF = {};
      subSnap.docs.forEach(d => {
        const data = d.data();
        let fid = null;
        if (data.franchiseeRef) {
          if (data.franchiseeRef.id) fid = data.franchiseeRef.id;
          else if (data.franchiseeRef.path) fid = data.franchiseeRef.path.split('/').pop();
        }
        if (fid) subByF[fid] = { id: d.id, ...data };
      });
      setSubmissions(subByF);

      // ── T-shirt orders for the selected month/year ──
      // Orders are filtered by orderDate (month/year) and includeInMonthEnd
      const allOrdersSnap = await getDocs(collection(db, 'tshirtOrders'));
      const ordersByF = {};
      allOrdersSnap.docs.forEach(d => {
        const data = d.data();
        const dt = data.orderDate?.toDate ? data.orderDate.toDate() : (data.orderDate ? new Date(data.orderDate) : null);
        if (!dt) return;
        if (dt.getMonth() !== selectedMonth || dt.getFullYear() !== selectedYear) return;
        // includeInMonthEnd defaults to true if undefined
        if (data.includeInMonthEnd === false) return;

        let fid = null;
        if (data.franchiseeRef) {
          if (data.franchiseeRef.id) fid = data.franchiseeRef.id;
          else if (data.franchiseeRef.path) fid = data.franchiseeRef.path.split('/').pop();
        }
        if (!fid) return;

        if (!ordersByF[fid]) ordersByF[fid] = [];
        ordersByF[fid].push({ id: d.id, ...data, orderDateParsed: dt });
      });
      setTshirtOrdersByF(ordersByF);

      // Reset per-row state for new month
      setRoyaltyState({});
      setTshirtState({});
      setCourierState({});
      setTshirtQty({});
      setDraftStatus('idle');
      setDraftSavedAt(null);

      // Try to load any saved draft for this month
      try {
        const draftSnap = await getDoc(doc(db, 'settings', draftDocId(selectedYear, selectedMonth)));
        if (draftSnap.exists()) {
          const d = draftSnap.data();
          if (d.royaltyState && typeof d.royaltyState === 'object') setRoyaltyState(d.royaltyState);
          if (d.tshirtState  && typeof d.tshirtState  === 'object') setTshirtState(d.tshirtState);
          if (d.tshirtQty    && typeof d.tshirtQty    === 'object') setTshirtQty(d.tshirtQty);
          if (d.courierState && typeof d.courierState === 'object') setCourierState(d.courierState);
          if (d.invoiceDate)                                        setInvoiceDate(d.invoiceDate);
          if (d.courierInvoiceDate)                                 setCourierInvoiceDate(d.courierInvoiceDate);
          if (typeof d.termsDays === 'number')                      setTermsDays(d.termsDays);
          if (d.savedAt?.toDate) {
            setDraftSavedAt(d.savedAt.toDate());
            setDraftStatus('saved');
          }
        }
      } catch (draftErr) {
        // Drafts are best-effort; silently ignore failures
        console.warn('Could not load draft for this month:', draftErr.message);
      }
    } catch (err) {
      console.error('Month data load error:', err);
      alert('Could not load month data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ─── Computed: list of franchisees who would be included (not test, has QB name) ──
  const eligibleFranchisees = useMemo(() => {
    return franchisees.filter(f => !f.isTest);
  }, [franchisees]);

  const unmappedFranchisees = useMemo(() => {
    return eligibleFranchisees.filter(f => !f.quickbooksCustomerName || !f.quickbooksCustomerName.trim());
  }, [eligibleFranchisees]);

  // ─── Computed: Royalty rows ────────────────────────────────────────────────
  const royaltyRows = useMemo(() => {
    return eligibleFranchisees
      .map(f => {
        const sub = submissions[f.id];
        const royaltyIncVat = sub?.royaltyAmount || 0;
        const vat = f.vatRate ?? 15;
        const exVat = roundMoney(royaltyIncVat / vatDivisor(vat));
        return {
          franchisee: f,
          submission: sub,
          royaltyIncVat,
          exVat,
          vat,
          hasAmount: royaltyIncVat > 0,
          mapped: !!(f.quickbooksCustomerName && f.quickbooksCustomerName.trim()),
        };
      })
      .filter(r => r.hasAmount)
      .sort((a, b) => (a.franchisee.name || '').localeCompare(b.franchisee.name || ''));
  }, [eligibleFranchisees, submissions]);

  // ─── Computed: T-shirt rows (with breakdown per franchisee) ────────────────
  // Quantities can be overridden in `tshirtQty` state. Pre-fill defaults
  // come from BABS T-shirt orders, but ALL eligible franchisees are shown
  // so the user can manually add quantities for those who didn't order via BABS.
  const tshirtRows = useMemo(() => {
    const cleanInt = (v) => {
      if (v === '' || v === null || v === undefined) return 0;
      const n = parseInt(v, 10);
      return isNaN(n) ? 0 : Math.max(0, n);
    };

    return eligibleFranchisees
      .map(f => {
        const orders = tshirtOrdersByF[f.id] || [];

        // BABS-derived (auto) quantities
        let babsKids = 0, babsSL = 0, babsXL = 0, babsStickers = 0;
        orders.forEach(o => {
          babsKids += (o.studentTshirts || []).length;
          if (o.extraStock) {
            Object.values(o.extraStock).forEach(qty => { babsKids += (qty || 0); });
          }
          const slSizes = o.adultTshirts?.smallToLarge?.sizes || {};
          babsSL += (slSizes.S || 0) + (slSizes.M || 0) + (slSizes.L || 0);
          const xlSizes = o.adultTshirts?.xlToXxxxl?.sizes || {};
          babsXL += (xlSizes.XL || 0) + (xlSizes.XXL || 0) + (xlSizes.XXXL || 0);
          babsStickers += (o.stickers?.quantity || 0);
        });

        // Apply user overrides if any
        const ov = tshirtQty[f.id] || {};
        const kidsCount    = ov.kids     !== undefined ? cleanInt(ov.kids)     : babsKids;
        const adultSLCount = ov.adultSL  !== undefined ? cleanInt(ov.adultSL)  : babsSL;
        const adultXLCount = ov.adultXL  !== undefined ? cleanInt(ov.adultXL)  : babsXL;
        const stickerCount = ov.stickers !== undefined ? cleanInt(ov.stickers) : babsStickers;

        const vat = f.vatRate ?? 15;
        const div = vatDivisor(vat);

        // Math note: line.amount is the EX-VAT amount that goes into the QB CSV.
        // It MUST back-calculate cleanly to the inc-VAT amount when QB adds 15%.
        // So we compute:  incAmount = qty × inc_price   (e.g. 20 × R120 = R2400 - clean)
        //                 exAmount  = incAmount ÷ 1.15  (e.g. R2400 ÷ 1.15 = R2086.96)
        // Then QB: 2086.96 + 15% = 2400.00 ✓
        // (If we instead used qty × rounded_ex_rate, e.g. 20 × R104.35 = R2087, then
        //  QB would back-calc 2087 + 15% = R2400.05 - 5c off!)
        const lines = [];
        if (kidsCount > 0) {
          const incPrice  = QB_PRICES_INC.kidsTshirt;
          const incAmount = roundMoney(kidsCount * incPrice);
          const exAmount  = roundMoney(incAmount / div);
          lines.push({
            item: QB_ITEMS.kidsTshirt, qty: kidsCount,
            rate:      roundMoney(incPrice / div),
            amount:    exAmount,
            incAmount: incAmount,
          });
        }
        if (adultSLCount > 0) {
          const incPrice  = QB_PRICES_INC.adultTshirtSL;
          const incAmount = roundMoney(adultSLCount * incPrice);
          const exAmount  = roundMoney(incAmount / div);
          lines.push({
            item: QB_ITEMS.adultTshirtSL, qty: adultSLCount,
            rate:      roundMoney(incPrice / div),
            amount:    exAmount,
            incAmount: incAmount,
          });
        }
        if (adultXLCount > 0) {
          const incPrice  = QB_PRICES_INC.adultTshirtXL;
          const incAmount = roundMoney(adultXLCount * incPrice);
          const exAmount  = roundMoney(incAmount / div);
          lines.push({
            item: QB_ITEMS.adultTshirtXL, qty: adultXLCount,
            rate:      roundMoney(incPrice / div),
            amount:    exAmount,
            incAmount: incAmount,
          });
        }
        if (stickerCount > 0) {
          const incPrice  = QB_PRICES_INC.stickers;
          const incAmount = roundMoney(stickerCount * incPrice);
          const exAmount  = roundMoney(incAmount / div);
          lines.push({
            item: QB_ITEMS.stickers, qty: stickerCount,
            rate:      roundMoney(incPrice / div),
            amount:    exAmount,
            incAmount: incAmount,
          });
        }

        const totalExVat  = roundMoney(lines.reduce((s, l) => s + l.amount,    0));
        const totalIncVat = roundMoney(lines.reduce((s, l) => s + l.incAmount, 0));
        const totalVat    = roundMoney(totalIncVat - totalExVat);
        const fromBabs    = babsKids > 0 || babsSL > 0 || babsXL > 0 || babsStickers > 0;

        return {
          franchisee: f,
          orders,
          counts:     { kidsCount, adultSLCount, adultXLCount, stickerCount },
          babsCounts: { kids: babsKids, adultSL: babsSL, adultXL: babsXL, stickers: babsStickers },
          lines,
          totalExVat,
          totalVat,
          totalIncVat,
          vat,
          hasItems: lines.length > 0,
          fromBabs,
          mapped: !!(f.quickbooksCustomerName && f.quickbooksCustomerName.trim()),
        };
      })
      // No filter — every eligible franchisee is shown so user can manually add quantities
      .sort((a, b) => (a.franchisee.name || '').localeCompare(b.franchisee.name || ''));
  }, [eligibleFranchisees, tshirtOrdersByF, tshirtQty]);

  // ─── Computed: Courier rows (all eligible franchisees, user enters amounts) ─
  const courierRows = useMemo(() => {
    return eligibleFranchisees
      .map(f => ({
        franchisee: f,
        vat: f.vatRate ?? 15,
        mapped: !!(f.quickbooksCustomerName && f.quickbooksCustomerName.trim()),
      }))
      .sort((a, b) => (a.franchisee.name || '').localeCompare(b.franchisee.name || ''));
  }, [eligibleFranchisees]);

  // ─── Computed: how many invoices will be generated ────────────────────────
  const summary = useMemo(() => {
    let royaltyCount = 0;
    let tshirtCount  = 0;
    let courierCount = 0;
    let totalExVat   = 0;
    let totalVat     = 0;
    let totalIncVat  = 0;

    royaltyRows.forEach(r => {
      const st = royaltyState[r.franchisee.id];
      const include = st?.include !== false;
      const override = st?.amountOverride;
      const hasOverride = override !== undefined && override !== null && override !== '';
      const exVat = hasOverride ? Number(override) : r.exVat;
      if (!(include && r.mapped && exVat > 0)) return;
      // Inc VAT: if override, recompute from ex VAT; if not, use SOD directly
      const incVat = hasOverride ? roundMoney(exVat * vatDivisor(r.vat)) : r.royaltyIncVat;
      const vatAmt = roundMoney(incVat - exVat);
      royaltyCount++;
      totalExVat  += exVat;
      totalVat    += vatAmt;
      totalIncVat += incVat;
    });

    tshirtRows.forEach(r => {
      const st = tshirtState[r.franchisee.id];
      const include = st?.include !== false;
      if (!(include && r.mapped && r.lines.length > 0)) return;
      tshirtCount++;
      totalExVat  += r.totalExVat;
      totalVat    += r.totalVat;
      totalIncVat += r.totalIncVat;
    });

    courierRows.forEach(r => {
      const st = courierState[r.franchisee.id];
      const include = !!st?.include;
      const incAmt = parseFloat(st?.amountIncVat);
      if (!(include && r.mapped && incAmt > 0)) return;
      const exVat = roundMoney(incAmt / vatDivisor(r.vat));
      const vatAmt = roundMoney(incAmt - exVat);
      courierCount++;
      totalExVat  += exVat;
      totalVat    += vatAmt;
      totalIncVat += incAmt;
    });

    return {
      royaltyCount,
      tshirtCount,
      courierCount,
      totalInvoices: royaltyCount + tshirtCount + courierCount,
      totalExVat:  roundMoney(totalExVat),
      totalVat:    roundMoney(totalVat),
      totalIncVat: roundMoney(totalIncVat),
    };
  }, [royaltyRows, tshirtRows, courierRows, royaltyState, tshirtState, courierState]);

  // ─── Build the list of invoices to write ───────────────────────────────────
  const buildInvoices = () => {
    let nextNum = lastInvoiceNumber + 1;

    // Dates for royalty + t-shirts
    const invDate = new Date(invoiceDate);
    const dueDate = new Date(invDate);
    dueDate.setDate(dueDate.getDate() + Number(termsDays));
    const invDateStr = formatDDMMYYYY(invDate);
    const dueDateStr = formatDDMMYYYY(dueDate);

    // Dates for courier (may be a different date — Linda couriers throughout the month)
    const courInvDate = new Date(courierInvoiceDate);
    const courDueDate = new Date(courInvDate);
    courDueDate.setDate(courDueDate.getDate() + Number(termsDays));
    const courInvDateStr = formatDDMMYYYY(courInvDate);
    const courDueDateStr = formatDDMMYYYY(courDueDate);

    const monthLabel = `${monthNames[selectedMonth]} ${selectedYear}`;
    const termsStr   = `Net ${termsDays}`;

    const invoices = [];

    // Royalty invoices
    royaltyRows.forEach(r => {
      const st = royaltyState[r.franchisee.id];
      const include = st?.include !== false;
      if (!include || !r.mapped) return;
      const override = st?.amountOverride;
      const hasOverride = override !== undefined && override !== null && override !== '';
      const amount = hasOverride ? Number(override) : r.exVat;
      if (!(amount > 0)) return;
      // incAmount: with override, recompute from ex; without override, use original SOD (the bank amount)
      const incAmount = hasOverride
        ? roundMoney(amount * vatDivisor(r.vat))
        : r.royaltyIncVat;
      invoices.push({
        invoiceNo: `${invoicePrefix}${nextNum++}`,
        customer:  r.franchisee.quickbooksCustomerName,
        babsName:  r.franchisee.name,
        vat:       r.vat,
        invoiceDate: invDateStr,
        dueDate:     dueDateStr,
        terms:       termsStr,
        memo:        `${monthLabel} Royalties`,
        type:        'Royalty',
        lines: [{
          item: QB_ITEMS.royalty, desc: '', qty: 1,
          rate:      roundMoney(amount),
          amount:    roundMoney(amount),
          incAmount: incAmount,
        }],
      });
    });

    // T-shirt invoices
    tshirtRows.forEach(r => {
      const st = tshirtState[r.franchisee.id];
      const include = st?.include !== false;
      if (!include || !r.mapped || r.lines.length === 0) return;
      invoices.push({
        invoiceNo: `${invoicePrefix}${nextNum++}`,
        customer:  r.franchisee.quickbooksCustomerName,
        babsName:  r.franchisee.name,
        vat:       r.vat,
        invoiceDate: invDateStr,
        dueDate:     dueDateStr,
        terms:       termsStr,
        memo:        `${monthLabel} T-Shirt Order`,
        type:        'T-Shirts',
        lines: r.lines.map(l => ({ ...l, desc: '' })),
      });
    });

    // Courier invoices
    courierRows.forEach(r => {
      const st = courierState[r.franchisee.id];
      const include = !!st?.include;
      const incAmt = parseFloat(st?.amountIncVat);
      if (!include || !r.mapped || !(incAmt > 0)) return;
      const exVat = roundMoney(incAmt / vatDivisor(r.vat));
      invoices.push({
        invoiceNo: `${invoicePrefix}${nextNum++}`,
        customer:  r.franchisee.quickbooksCustomerName,
        babsName:  r.franchisee.name,
        vat:       r.vat,
        invoiceDate: courInvDateStr,  // ← courier-specific date
        dueDate:     courDueDateStr,  // ← courier-specific due date
        terms:       termsStr,
        memo:        `${monthLabel} Courier`,
        type:        'Courier',
        lines: [{
          item: QB_ITEMS.courier, desc: '', qty: 1,
          rate:      exVat,
          amount:    exVat,
          incAmount: roundMoney(incAmt),
        }],
      });
    });

    return invoices;
  };

  // ─── Generate CSV from invoices ────────────────────────────────────────────
  // Matches Helena's QuickBooks CSV template exactly:
  // First line of a multi-line invoice has customer/dates/terms/memo;
  // subsequent lines just have the invoice number + line item details.
  // QB groups all rows with the same InvoiceNo into a single invoice.
  const generateCsv = (invoices) => {
    // Fixed text the accountant wants in the Memo column on every invoice.
    const MEMO_TEXT = 'Kindly note that payment is due on receipt of invoice.';

    // Turn a VAT rate into the QuickBooks tax-code label.
    // ⚠️ These two strings must match EXACTLY what QuickBooks expects.
    //    If QB rejects the import, check the wording here first (especially
    //    the zero-rated one for Michelle / Namibia).
    const taxCodeFor = (vatRate) =>
      (vatRate || 0) > 0 ? 'Standard (15%)' : 'Exempt';

    // VAT amount for a line = line amount × vat rate. Michelle (0%) → 0.
    const taxAmountFor = (amount, vatRate) =>
      roundMoney((Number(amount) || 0) * ((vatRate || 0) / 100));

    const header = [
      '*InvoiceNo', '*Customer', '*InvoiceDate', '*DueDate', 'Terms',
      'Location', 'Memo', 'Item(Product/Service)', 'ItemDescription',
      'ItemQuantity', 'ItemRate', '*ItemAmount', '*ItemTaxCode', 'ItemTaxAmount', 'Service Date'
    ];
    const rows = [header];

    invoices.forEach(inv => {
      const taxCode = taxCodeFor(inv.vat);
      // The old "memo" (e.g. "May 2026 Royalties") now goes into ItemDescription
      // on every line, matching the accountant's file.
      const description = inv.memo;

      inv.lines.forEach((line, idx) => {
        const taxAmt = taxAmountFor(line.amount, inv.vat);
        if (idx === 0) {
          rows.push([
            inv.invoiceNo, inv.customer, inv.invoiceDate, inv.dueDate, inv.terms,
            '', MEMO_TEXT, line.item, description, line.qty, line.rate, line.amount,
            taxCode, taxAmt, inv.invoiceDate,
          ]);
        } else {
          rows.push([
            inv.invoiceNo, '', '', '', '',
            '', '', line.item, description, line.qty, line.rate, line.amount,
            taxCode, taxAmt, '',
          ]);
        }
      });
    });

    return rows.map(r => r.map(csvEscape).join(',')).join('\n');
  };

  // ─── Generate Payment Breakdown XLSX ───────────────────────────────────────
  const generateBreakdown = (invoices, monthSlug) => {
    // Group by customer
    const byCustomer = {};
    invoices.forEach(inv => {
      if (!byCustomer[inv.customer]) byCustomer[inv.customer] = [];
      byCustomer[inv.customer].push(inv);
    });

    const rows = [];
    let grandExVat = 0, grandVat = 0, grandIncVat = 0;

    // Sort customers alphabetically
    Object.keys(byCustomer).sort().forEach(customer => {
      const invs = byCustomer[customer];
      let custExVat = 0, custVat = 0, custIncVat = 0;
      const babsName = invs[0].babsName || '';

      invs.forEach((inv, idx) => {
        const exVat  = roundMoney(inv.lines.reduce((s, l) => s + l.amount, 0));
        // Use exact incAmount from each line (avoids cent-level rounding errors).
        // All line types now carry incAmount: royalty (= SOD), tshirts (= qty × inc price), courier (= user entry).
        const incVat = roundMoney(inv.lines.reduce((s, l) => s + (l.incAmount || 0), 0));
        const vatAmt = roundMoney(incVat - exVat);
        custExVat += exVat; custVat += vatAmt; custIncVat += incVat;

        rows.push({
          'QuickBooks Customer': idx === 0 ? customer  : '',
          'BABS Name':           idx === 0 ? babsName : '',
          'Invoice No':          inv.invoiceNo,
          'Type':                inv.type,
          'Memo':                inv.memo,
          'Ex VAT (R)':          exVat,
          'VAT (R)':             vatAmt,
          'Inc VAT / Total (R)': incVat,
        });
      });

      // Customer subtotal
      rows.push({
        'QuickBooks Customer': '',
        'BABS Name':           '',
        'Invoice No':          '',
        'Type':                '',
        'Memo':                '↳ Customer Total',
        'Ex VAT (R)':          roundMoney(custExVat),
        'VAT (R)':             roundMoney(custVat),
        'Inc VAT / Total (R)': roundMoney(custIncVat),
      });
      // Blank spacer
      rows.push({});

      grandExVat += custExVat; grandVat += custVat; grandIncVat += custIncVat;
    });

    // Grand total
    rows.push({
      'QuickBooks Customer': 'GRAND TOTAL',
      'BABS Name':           '',
      'Invoice No':          '',
      'Type':                '',
      'Memo':                '',
      'Ex VAT (R)':          roundMoney(grandExVat),
      'VAT (R)':             roundMoney(grandVat),
      'Inc VAT / Total (R)': roundMoney(grandIncVat),
    });

    const ws = XLSX.utils.json_to_sheet(rows, {
      header: ['QuickBooks Customer', 'BABS Name', 'Invoice No', 'Type', 'Memo', 'Ex VAT (R)', 'VAT (R)', 'Inc VAT / Total (R)']
    });
    ws['!cols'] = [
      { wch: 36 }, { wch: 22 }, { wch: 14 }, { wch: 12 },
      { wch: 28 }, { wch: 13 }, { wch: 11 }, { wch: 17 }
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Payment Breakdown');
    XLSX.writeFile(wb, `BABS_Payment_Breakdown_${monthSlug}.xlsx`);
  };

  // ─── Generate everything ───────────────────────────────────────────────────
  const handleGenerate = async () => {
    const invoices = buildInvoices();

    if (invoices.length === 0) {
      alert('No invoices to generate.\n\nTick at least one franchisee in any tab and make sure amounts are filled in.');
      return;
    }

    const firstNo = invoices[0].invoiceNo;
    const lastNo  = invoices[invoices.length - 1].invoiceNo;
    const totalExVat = roundMoney(invoices.reduce((s, i) => s + i.lines.reduce((ls, l) => ls + l.amount, 0), 0));

    const sameDate = courierInvoiceDate === invoiceDate;
    const dateLines = sameDate
      ? `📅 Invoice date: ${formatDDMMYYYY(new Date(invoiceDate))}\n`
      : `📅 Royalty/T-shirt date: ${formatDDMMYYYY(new Date(invoiceDate))}\n📦 Courier date: ${formatDDMMYYYY(new Date(courierInvoiceDate))}\n`;

    const confirmMsg =
      `Generate ${invoices.length} invoice(s)?\n\n` +
      `📋 Invoice numbers: ${firstNo} → ${lastNo}\n` +
      dateLines +
      `💰 Total ex VAT: R${totalExVat.toFixed(2)}\n\n` +
      `Two files will be downloaded:\n` +
      `  • CSV (for QuickBooks import)\n` +
      `  • XLSX (Payment Breakdown for Helena)\n\n` +
      `After this, the last invoice number will be updated to ${lastNo}.`;

    if (!window.confirm(confirmMsg)) return;

    setGenerating(true);
    try {
      const monthSlug = `${monthNames[selectedMonth]}${selectedYear}`;

      // 1. CSV
      const csv = generateCsv(invoices);
      downloadFile(csv, `BABS_Invoices_${monthSlug}.csv`, 'text/csv;charset=utf-8;');

      // 2. XLSX
      generateBreakdown(invoices, monthSlug);

      // 3. Update lastInvoiceNumber in Firestore (best-effort - works only if Firestore rules allow)
      const newLast = lastInvoiceNumber + invoices.length;
      let settingsSaved = true;
      try {
        await setDoc(doc(db, 'settings', 'invoicing'), {
          lastInvoiceNumber: newLast,
          invoicePrefix,
          lastGeneratedAt:   Timestamp.now(),
          lastGeneratedFor:  `${monthNames[selectedMonth]} ${selectedYear}`,
          lastGeneratedCount: invoices.length,
        }, { merge: true });
      } catch (settingsErr) {
        settingsSaved = false;
        console.warn('Could not save invoice counter to Firestore. Update the "Last Invoice #" field manually next time.', settingsErr.message);
      }

      setLastInvoiceNumber(newLast);

      // Note: draft is NOT deleted - kept in case user spots a mistake and needs to
      // re-generate. To redo a month: edit values, set "Last Invoice #" back to the
      // value before the previous Generate, and click Generate again.

      alert(
        `✅ Done!\n\n${invoices.length} invoice(s) generated.\n\n` +
        `Two files downloaded:\n` +
        `  • BABS_Invoices_${monthSlug}.csv\n` +
        `  • BABS_Payment_Breakdown_${monthSlug}.xlsx\n\n` +
        `Next invoice number is now ${invoicePrefix}${newLast + 1}.` +
        (settingsSaved
          ? ''
          : '\n\n⚠️ Note: could not save the counter to Firestore (rules may not allow access to /settings/). Remember to update the "Last Invoice #" field manually before the next run.')
      );
    } catch (err) {
      console.error('Generate error:', err);
      alert('❌ Error: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  // ─── Toggle handlers ───────────────────────────────────────────────────────
  const toggleRoyalty = (id, include) => {
    setRoyaltyState(prev => ({ ...prev, [id]: { ...prev[id], include } }));
  };
  const setRoyaltyAmount = (id, value) => {
    setRoyaltyState(prev => ({ ...prev, [id]: { ...prev[id], amountOverride: value } }));
  };
  const toggleTshirt = (id, include) => {
    setTshirtState(prev => ({ ...prev, [id]: { ...prev[id], include } }));
  };
  const setTshirtQtyField = (id, field, value) => {
    setTshirtQty(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };
  const toggleCourier = (id, include) => {
    setCourierState(prev => ({ ...prev, [id]: { ...prev[id], include } }));
  };
  const setCourierAmount = (id, value) => {
    setCourierState(prev => ({ ...prev, [id]: { ...prev[id], amountIncVat: value, include: value && parseFloat(value) > 0 } }));
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  if (loading && franchisees.length === 0) {
    return <div className="ie-loading">Loading invoice export...</div>;
  }

  const monthLabel = `${monthNames[selectedMonth]} ${selectedYear}`;

  return (
    <div className="ie-container">

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="ie-header">
        <div>
          <h1>📤 Invoice Export</h1>
          <p className="subtitle">
            Generate QuickBooks import CSV + Payment Breakdown for Helena
          </p>
        </div>
        <div className="header-right">
          <DraftStatusBadge status={draftStatus} savedAt={draftSavedAt} />
          <button onClick={onClose} className="back-btn">← Back to Dashboard</button>
        </div>
      </div>

      {/* ── Settings bar ────────────────────────────────────────────────── */}
      <div className="ie-settings-card">
        <div className="settings-row">
          <div className="setting-group">
            <label>Month</label>
            <select value={selectedMonth} onChange={e => setSelectedMonth(parseInt(e.target.value))}>
              {monthNames.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
          </div>
          <div className="setting-group">
            <label>Year</label>
            <select value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))}>
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="setting-group">
            <label>Invoice Date</label>
            <input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} title="Used for Royalty and T-shirt invoices" />
            <small className="hint">For royalty + t-shirts</small>
          </div>
          <div className="setting-group">
            <label>Courier Date</label>
            <input type="date" value={courierInvoiceDate} onChange={e => setCourierInvoiceDate(e.target.value)} title="Used for Courier invoices only" />
            <small className="hint">For courier only</small>
          </div>
          <div className="setting-group">
            <label>Terms (days)</label>
            <input type="number" value={termsDays} min="0" max="120" onChange={e => setTermsDays(parseInt(e.target.value) || 0)} />
          </div>
          <div className="setting-group">
            <label>Invoice Prefix</label>
            <input type="text" value={invoicePrefix} onChange={e => setInvoicePrefix(e.target.value)} />
          </div>
          <div className="setting-group">
            <label>Last Invoice #</label>
            <input
              type="number"
              value={lastInvoiceNumber}
              onChange={e => setLastInvoiceNumber(parseInt(e.target.value) || 0)}
              title="Next invoice will be one above this"
            />
            <small className="hint">Next: <strong>{invoicePrefix}{lastInvoiceNumber + 1}</strong></small>
          </div>
        </div>
      </div>

      {/* ── Unmapped warning ─────────────────────────────────────────────── */}
      {unmappedFranchisees.length > 0 && (
        <div className="ie-warning">
          <strong>⚠️ {unmappedFranchisees.length} franchisee(s) will be skipped — no QuickBooks customer name set:</strong>
          <ul>
            {unmappedFranchisees.map(f => <li key={f.id}>{f.name || f.id}</li>)}
          </ul>
          <p className="warning-hint">Go to <strong>QuickBooks Customer Setup</strong> to fix these.</p>
        </div>
      )}

      {/* ── Summary cards ────────────────────────────────────────────────── */}
      <div className="ie-summary">
        <div className="sum-card card-blue">
          <div className="sum-label">Royalty Invoices</div>
          <div className="sum-value">{summary.royaltyCount}</div>
        </div>
        <div className="sum-card card-pink">
          <div className="sum-label">T-Shirt Invoices</div>
          <div className="sum-value">{summary.tshirtCount}</div>
        </div>
        <div className="sum-card card-orange">
          <div className="sum-label">Courier Invoices</div>
          <div className="sum-value">{summary.courierCount}</div>
        </div>
        <div className="sum-card card-green">
          <div className="sum-label">Total Invoices</div>
          <div className="sum-value">{summary.totalInvoices}</div>
        </div>
        <div className="sum-card card-purple">
          <div className="sum-label">Total Ex VAT</div>
          <div className="sum-value">{formatR(summary.totalExVat)}</div>
        </div>
        <div className="sum-card card-grey">
          <div className="sum-label">Total VAT</div>
          <div className="sum-value">{formatR(summary.totalVat)}</div>
        </div>
        <div className="sum-card card-red">
          <div className="sum-label">💰 Total Inc VAT<br/><small>(Bank Amount)</small></div>
          <div className="sum-value">{formatR(summary.totalIncVat)}</div>
        </div>
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────────── */}
      <div className="ie-tabs">
        <button className={activeTab === 'royalty' ? 'active' : ''} onClick={() => setActiveTab('royalty')}>
          💰 Royalty ({royaltyRows.length})
        </button>
        <button className={activeTab === 'tshirt' ? 'active' : ''} onClick={() => setActiveTab('tshirt')}>
          👕 T-Shirts ({tshirtRows.filter(r => r.hasItems).length})
        </button>
        <button className={activeTab === 'courier' ? 'active' : ''} onClick={() => setActiveTab('courier')}>
          📦 Courier ({courierRows.length})
        </button>
      </div>

      {/* ── Tab content ──────────────────────────────────────────────────── */}
      <div className="ie-tab-content">

        {/* ── ROYALTY ── */}
        {activeTab === 'royalty' && (
          <>
            <p className="tab-intro">
              One row per franchisee with a <strong>{monthLabel}</strong> Month-End Submission. Amounts auto-calculated as <strong>SOD royalty ÷ (1 + VAT%)</strong>.
              The <strong>Inc VAT</strong> column is the amount you'll see hit the bank. Tick to include. Edit the ex-VAT amount if you need to override.
            </p>
            {royaltyRows.length === 0 ? (
              <div className="empty-state">No royalty submissions found for {monthLabel}.</div>
            ) : (
              <table className="ie-table">
                <thead>
                  <tr>
                    <th style={{width:'46px'}}></th>
                    <th>Franchisee</th>
                    <th>QB Customer</th>
                    <th className="th-center">VAT</th>
                    <th className="th-right">Ex VAT (for QB)</th>
                    <th className="th-right">VAT Amount</th>
                    <th className="th-right inc-vat-col">Inc VAT (Bank)</th>
                    <th>Override (Ex VAT)</th>
                  </tr>
                </thead>
                <tbody>
                  {royaltyRows.map(r => {
                    const st = royaltyState[r.franchisee.id] || {};
                    const include = st.include !== false;
                    const override = st.amountOverride;
                    const hasOverride = override !== undefined && override !== null && override !== '';
                    const exVat   = hasOverride ? Number(override) : r.exVat;
                    const incVat  = hasOverride ? roundMoney(exVat * vatDivisor(r.vat)) : r.royaltyIncVat;
                    const vatAmt  = roundMoney(incVat - exVat);
                    return (
                      <tr key={r.franchisee.id} className={!r.mapped ? 'row-unmapped' : ''}>
                        <td>
                          <input type="checkbox" disabled={!r.mapped} checked={include && r.mapped} onChange={e => toggleRoyalty(r.franchisee.id, e.target.checked)} />
                        </td>
                        <td className="cell-name">{r.franchisee.name}</td>
                        <td className="cell-qb">{r.franchisee.quickbooksCustomerName || <span className="muted">— not mapped —</span>}</td>
                        <td className="th-center"><span className="vat-pill">{r.vat}%</span></td>
                        <td className="th-right strong">{formatR(exVat)}</td>
                        <td className="th-right vat-amt-col">{formatR(vatAmt)}</td>
                        <td className="th-right inc-vat-col strong">{formatR(incVat)}</td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            placeholder={`default ${formatR(r.exVat)}`}
                            className="amount-input"
                            value={st.amountOverride ?? ''}
                            onChange={e => setRoyaltyAmount(r.franchisee.id, e.target.value)}
                            disabled={!r.mapped}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        )}

        {/* ── T-SHIRTS ── */}
        {activeTab === 'tshirt' && (
          <>
            <p className="tab-intro">
              Enter quantities for each franchisee. Rows pre-filled from BABS orders for <strong>{monthLabel}</strong> are tagged <strong>📦 BABS</strong>.
              For franchisees who didn't submit through the system (e.g. manual orders), type the quantities directly. Rows with all zeros won't generate an invoice.
            </p>
            <table className="ie-table">
              <thead>
                <tr>
                  <th style={{width:'46px'}}></th>
                  <th>Franchisee</th>
                  <th>QB Customer</th>
                  <th className="th-center">Kids<br/><small className="hdr-price">@ R120</small></th>
                  <th className="th-center">Adult S-L<br/><small className="hdr-price">@ R120</small></th>
                  <th className="th-center">Adult XL+<br/><small className="hdr-price">@ R130</small></th>
                  <th className="th-center">Stickers<br/><small className="hdr-price">@ R70</small></th>
                  <th className="th-center">VAT</th>
                  <th className="th-right">Ex VAT (for QB)</th>
                  <th className="th-right">VAT Amount</th>
                  <th className="th-right inc-vat-col">Inc VAT (Bank)</th>
                </tr>
              </thead>
              <tbody>
                {tshirtRows.map(r => {
                  const st = tshirtState[r.franchisee.id] || {};
                  const ov = tshirtQty[r.franchisee.id] || {};
                  const totalQty = r.counts.kidsCount + r.counts.adultSLCount + r.counts.adultXLCount + r.counts.stickerCount;
                  const includeDefault = totalQty > 0;
                  const include = st.include !== undefined ? st.include : includeDefault;
                  // value to show in each input: override if set (even if ''), else BABS-derived count
                  const valOf = (field, babsVal) => ov[field] !== undefined ? ov[field] : babsVal;
                  return (
                    <tr key={r.franchisee.id} className={!r.mapped ? 'row-unmapped' : (totalQty === 0 ? 'row-empty' : '')}>
                      <td>
                        <input
                          type="checkbox"
                          disabled={!r.mapped || totalQty === 0}
                          checked={include && r.mapped && totalQty > 0}
                          onChange={e => toggleTshirt(r.franchisee.id, e.target.checked)}
                        />
                      </td>
                      <td className="cell-name">
                        {r.franchisee.name}
                        {r.fromBabs && <span className="babs-pill" title="Quantities pre-filled from BABS T-shirt orders">📦 BABS</span>}
                      </td>
                      <td className="cell-qb">{r.franchisee.quickbooksCustomerName || <span className="muted">— not mapped —</span>}</td>
                      <td className="th-center">
                        <input type="number" min="0" className="qty-input"
                          value={valOf('kids', r.babsCounts.kids)}
                          onChange={e => setTshirtQtyField(r.franchisee.id, 'kids', e.target.value)}
                          disabled={!r.mapped} />
                      </td>
                      <td className="th-center">
                        <input type="number" min="0" className="qty-input"
                          value={valOf('adultSL', r.babsCounts.adultSL)}
                          onChange={e => setTshirtQtyField(r.franchisee.id, 'adultSL', e.target.value)}
                          disabled={!r.mapped} />
                      </td>
                      <td className="th-center">
                        <input type="number" min="0" className="qty-input"
                          value={valOf('adultXL', r.babsCounts.adultXL)}
                          onChange={e => setTshirtQtyField(r.franchisee.id, 'adultXL', e.target.value)}
                          disabled={!r.mapped} />
                      </td>
                      <td className="th-center">
                        <input type="number" min="0" className="qty-input"
                          value={valOf('stickers', r.babsCounts.stickers)}
                          onChange={e => setTshirtQtyField(r.franchisee.id, 'stickers', e.target.value)}
                          disabled={!r.mapped} />
                      </td>
                      <td className="th-center"><span className="vat-pill">{r.vat}%</span></td>
                      <td className="th-right strong">{r.totalExVat > 0 ? formatR(r.totalExVat) : <span className="muted">—</span>}</td>
                      <td className="th-right vat-amt-col">{r.totalVat > 0 ? formatR(r.totalVat) : <span className="muted">—</span>}</td>
                      <td className="th-right inc-vat-col strong">{r.totalIncVat > 0 ? formatR(r.totalIncVat) : <span className="muted">—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}

        {/* ── COURIER ── */}
        {activeTab === 'courier' && (
          <>
            <p className="tab-intro">
              Enter the <strong>inc-VAT</strong> courier amount from your <strong>BobGo report</strong> for each franchisee who had a courier charge in {monthLabel}.
              Leave blank for those without a courier charge — they won't get a courier invoice.
              The ex-VAT amount (sent to QB) is auto-calculated.
            </p>
            <table className="ie-table">
              <thead>
                <tr>
                  <th style={{width:'46px'}}></th>
                  <th>Franchisee</th>
                  <th>QB Customer</th>
                  <th className="th-center">VAT</th>
                  <th>BobGo Amount (Inc VAT)</th>
                  <th className="th-right">Ex VAT (for QB)</th>
                  <th className="th-right">VAT Amount</th>
                  <th className="th-right inc-vat-col">Inc VAT (Bank)</th>
                </tr>
              </thead>
              <tbody>
                {courierRows.map(r => {
                  const st = courierState[r.franchisee.id] || {};
                  const include = !!st.include;
                  const incAmt = parseFloat(st.amountIncVat) || 0;
                  const exVat = incAmt > 0 ? roundMoney(incAmt / vatDivisor(r.vat)) : 0;
                  const vatAmt = incAmt > 0 ? roundMoney(incAmt - exVat) : 0;
                  return (
                    <tr key={r.franchisee.id} className={!r.mapped ? 'row-unmapped' : ''}>
                      <td>
                        <input type="checkbox" disabled={!r.mapped || incAmt <= 0} checked={include && r.mapped && incAmt > 0} onChange={e => toggleCourier(r.franchisee.id, e.target.checked)} />
                      </td>
                      <td className="cell-name">{r.franchisee.name}</td>
                      <td className="cell-qb">{r.franchisee.quickbooksCustomerName || <span className="muted">— not mapped —</span>}</td>
                      <td className="th-center"><span className="vat-pill">{r.vat}%</span></td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          className="amount-input wide"
                          value={st.amountIncVat ?? ''}
                          onChange={e => setCourierAmount(r.franchisee.id, e.target.value)}
                          disabled={!r.mapped}
                        />
                      </td>
                      <td className="th-right strong">{exVat > 0 ? formatR(exVat) : <span className="muted">—</span>}</td>
                      <td className="th-right vat-amt-col">{vatAmt > 0 ? formatR(vatAmt) : <span className="muted">—</span>}</td>
                      <td className="th-right inc-vat-col strong">{incAmt > 0 ? formatR(incAmt) : <span className="muted">—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}

      </div>

      {/* ── Generate footer ──────────────────────────────────────────────── */}
      <div className="ie-footer">
        <div className="footer-info">
          {summary.totalInvoices === 0
            ? <span className="muted">Nothing selected to generate yet.</span>
            : <>
                <strong>{summary.totalInvoices}</strong> invoice(s) ready
                {' '}({summary.royaltyCount} royalty, {summary.tshirtCount} t-shirt, {summary.courierCount} courier)
                {' '}— numbers <strong>{invoicePrefix}{lastInvoiceNumber + 1}</strong> to <strong>{invoicePrefix}{lastInvoiceNumber + summary.totalInvoices}</strong>
              </>
          }
        </div>
        <button
          className="generate-btn"
          onClick={handleGenerate}
          disabled={generating || summary.totalInvoices === 0}
        >
          {generating ? '⏳ Generating...' : `🚀 Generate ${summary.totalInvoices} Invoice(s)`}
        </button>
      </div>

    </div>
  );
}

export default HOInvoiceExport;